/**
 * Email service using Firebase Cloud Functions to interact with Resend.com
 * 
 * This service handles sending emails through Firebase Cloud Functions
 */
import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../utils/environmentConfig';

export interface ContactFormData {
  name: string;
  email: string;
  inquiryType: string;
  message: string;
  consent: boolean;
  toEmail: string; // Required field to specify recipient email
}

export interface EmailPayload {
  to: string | string[];
  from?: string;
  subject: string;
  html: string;
  cc?: string | string[];
  bcc?: string | string[];
  replyTo?: string;
  attachments?: Array<{
    filename: string;
    content: string;
    contentType: string;
  }>;
  text?: string;
  tags?: Array<{
    name: string;
    value: string;
  }>;
}

// Firebase Cloud Functions URL - Using environment configuration
const SEND_EMAIL_ENDPOINT = `${getApiBaseUrl()}/sendEmail`;

/**
 * Sends an email using the generic sendEmail Cloud Function
 * @param emailPayload The email data to send
 * @returns Promise with the send result
 */
const sendEmail = async (emailPayload: EmailPayload) => {
  logger.info('🚀 Starting email sending process');
  logger.debug('Email payload:', emailPayload);
  
  try {
    logger.info('📤 Sending request to Cloud Function');
    const response = await fetch(SEND_EMAIL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(emailPayload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('❌ Server responded with an error:', errorData);
      throw new Error(errorData.message || 'Network response was not ok');
    }

    const responseData = await response.json();
    logger.success('✅ Email sent successfully', responseData);
    return response;
  } catch (error) {
    logger.error('❌ Error sending email:', error);
    throw error;
  }
};

/**
 * Formats and sends a contact form submission as an email through the generic sendEmail function
 * @param formData Form data from the contact form
 * @returns Promise with the send result
 */
const sendContactFormEmail = async (data: ContactFormData) => {
  logger.info('🚀 Starting contact form submission process');
  logger.debug('Form data:', data);
  
  // Validate required fields including toEmail
  if (!data.toEmail) {
    logger.error('❌ Missing required toEmail field');
    throw new Error('Missing required destination email address');
  }

  const { name, email, inquiryType, message, consent, toEmail } = data;

  const inquiryTypeMap: Record<string, string> = {
    "membership": "Membership Application",
    "event": "Event Information",
    "safety": "Safety or Concern",
    "other": "Other Inquiry",
  };
  
  const inquiryTypeText = inquiryTypeMap[inquiryType] || inquiryType;

  // First email: to admin
  const adminEmailPayload: EmailPayload = {
    to: toEmail,
    subject: `New Contact Form: ${inquiryTypeText}`,
    html: `
      <h2>New Contact Form Submission</h2>
      <p><strong>Name:</strong> ${name}</p>
      <p><strong>Email:</strong> ${email}</p>
      <p><strong>Inquiry Type:</strong> ${inquiryTypeText}</p>
      <p><strong>Message:</strong></p>
      <p>${message.replace(/\n/g, "<br>")}</p>
      <p><strong>Consent Given:</strong> ${consent ? "Yes" : "No"}</p>
      <hr>
      <p><small>This message was sent via the BodySharing contact form.</small></p>
    `,
  };

  // Second email: confirmation to user
  const userEmailPayload: EmailPayload = {
    to: email,
    subject: "Thank you for contacting BodySharing",
    html: `
      <h2>Thank You for Contacting BodySharing</h2>
      <p>Hello ${name},</p>
      <p>We have received your inquiry about "${inquiryTypeText}" and will get back to you shortly.</p>
      <p>For your records, here is a copy of your message:</p>
      <blockquote>${message.replace(/\n/g, "<br>")}</blockquote>
      <p>Best regards,<br>The BodySharing Team</p>
    `,
  };

  try {
    // Send both emails in parallel
    logger.info('📧 Sending admin and confirmation emails');
    const [adminResponse] = await Promise.all([
      sendEmail(adminEmailPayload),
      sendEmail(userEmailPayload)
    ]);
    
    return adminResponse;
  } catch (error) {
    logger.error('❌ Error in contact form email process:', error);
    throw error;
  }
};

export { sendEmail, sendContactFormEmail };

export default sendEmail;